/**
 * Popup Component - Portal-Based Popup Container
 *
 * Provides consistent popup behavior across the app:
 * - Uses React portal to render at body level (escapes overflow/z-index issues)
 * - Fixed positioning calculated from trigger element
 * - Mouse enter/leave for keeping popup open
 */

import React, { useRef, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

export interface PopupProps {
  /** Whether the popup is visible */
  isVisible: boolean;
  /** Call when mouse enters the popup (to keep it open) */
  onMouseEnter: () => void;
  /** Call when mouse leaves the popup */
  onMouseLeave: () => void;
  /** Popup content */
  children: React.ReactNode;
  /** Additional classes for the content container */
  className?: string;
  /** Minimum width in pixels (default: 256) */
  minWidth?: string;
  /** Reference to the trigger element for positioning (required) */
  triggerRef: React.RefObject<HTMLElement | null>;
}

interface Position {
  top: number;
  left: number;
}

/**
 * Popup container using React portal for proper stacking
 *
 * Usage:
 * ```tsx
 * const popup = usePopup();
 * const triggerRef = useRef<HTMLSpanElement>(null);
 *
 * <span ref={triggerRef} {...popup.triggerProps}>Hover me</span>
 * <Popup isVisible={popup.isVisible} triggerRef={triggerRef} {...popup.popupProps}>
 *   <div>Popup content</div>
 * </Popup>
 * ```
 */
export function Popup({
  isVisible,
  onMouseEnter,
  onMouseLeave,
  children,
  className = '',
  minWidth = 'min-w-64',
  triggerRef,
}: PopupProps): React.ReactElement | null {
  const popupRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Position>({ top: 0, left: 0 });

  // Calculate position based on trigger element
  useLayoutEffect(() => {
    if (!isVisible || !triggerRef?.current) return;

    const trigger = triggerRef.current;
    const rect = trigger.getBoundingClientRect();

    // Position below the trigger with a small gap
    let top = rect.bottom + 8;
    let left = rect.left;

    // Adjust if popup would go off-screen right
    const popupWidth = 280; // approximate
    if (left + popupWidth > window.innerWidth - 16) {
      left = window.innerWidth - popupWidth - 16;
    }

    // Adjust if popup would go off-screen bottom
    const popupHeight = 200; // approximate
    if (top + popupHeight > window.innerHeight - 16) {
      // Position above instead
      top = rect.top - popupHeight - 8;
    }

    setPosition({ top, left });
  }, [isVisible, triggerRef]);

  if (!isVisible) {
    return null;
  }

  // Portal-based popup with fixed positioning
  const popup = (
    <div
      ref={popupRef}
      className={`fixed z-[9999] ${minWidth}`}
      style={{ top: position.top, left: position.left }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className={`bg-white border border-gray-200 shadow-lg p-3 ${className}`}>
        {children}
      </div>
    </div>
  );

  return createPortal(popup, document.body);
}

export default Popup;
